import React, { useEffect, useState } from "react";
import { api, peso, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function ProductsPage() {
  const { hasPerm } = useAuth();
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", price: 0, category_id: "", prep_station: "bar", description: "", sku: "" });

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/categories"), api.get("/products")]);
    setCats(c.data);
    setProducts(p.data);
    if (!form.category_id && c.data[0]) setForm((f) => ({ ...f, category_id: c.data[0].id }));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...form, price: parseFloat(form.price) || 0 });
      toast.success("Product created");
      setForm({ name: "", price: 0, category_id: form.category_id, prep_station: "bar", description: "", sku: "" });
      setCreating(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleSoldOut = async (p) => {
    try {
      await api.post(`/products/${p.id}/sold-out`, null, { params: { sold_out: !p.sold_out } });
      setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, sold_out: !p.sold_out } : x));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="products-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="tiny-label text-violet-400">Catalog</div>
          <h1 className="mt-1 text-4xl font-extrabold tracking-tight">Products</h1>
          <p className="mt-1 text-zinc-500">Bar, kitchen & bottle service items.</p>
        </div>
        {hasPerm("product.manage") && (
          <button onClick={() => setCreating(true)} data-testid="new-product-btn" className="h-11 px-4 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-semibold shadow-[0_0_20px_rgba(139,92,246,0.35)]">+ New product</button>
        )}
      </div>

      <div className="panel-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left tiny-label border-b border-white/10">
              <th className="p-3">Name</th>
              <th className="p-3">Category</th>
              <th className="p-3">Station</th>
              <th className="p-3">SKU</th>
              <th className="p-3 text-right">Price</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-zinc-500">Loading…</td></tr>}
            {products.map((p) => {
              const cat = cats.find((c) => c.id === p.category_id);
              return (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`product-row-${p.id}`}>
                  <td className="p-3 font-semibold text-white">{p.name}</td>
                  <td className="p-3 text-zinc-400">{cat?.name || "—"}</td>
                  <td className="p-3 text-zinc-400">{p.prep_station}</td>
                  <td className="p-3 mono text-xs text-zinc-500">{p.sku}</td>
                  <td className="p-3 text-right font-bold text-white">{peso(p.price)}</td>
                  <td className="p-3">{p.sold_out ? <span className="tiny-label text-red-400">SOLD OUT</span> : p.active ? <span className="tiny-label text-emerald-400">ACTIVE</span> : <span className="tiny-label text-zinc-500">INACTIVE</span>}</td>
                  <td className="p-3">
                    {hasPerm("product.manage") && (
                      <button onClick={() => toggleSoldOut(p)} data-testid={`toggle-soldout-${p.id}`} className="text-xs text-zinc-400 hover:text-white underline underline-offset-2">
                        {p.sold_out ? "Mark available" : "Mark sold out"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="new-product-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setCreating(false)}/>
          <form onSubmit={submit} className="relative w-full max-w-md bg-zinc-950 border-l border-white/10 h-full p-6 overflow-y-auto shadow-2xl">
            <div className="tiny-label text-violet-400">Catalog</div>
            <h2 className="text-2xl font-bold">New product</h2>
            <Field label="Name">
              <input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="new-product-name" className="input"/>
            </Field>
            <Field label="Category">
              <select value={form.category_id} onChange={(e)=>setForm({...form, category_id: e.target.value})} required className="input" data-testid="new-product-category">
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Prep station">
              <select value={form.prep_station} onChange={(e)=>setForm({...form, prep_station: e.target.value})} className="input" data-testid="new-product-station">
                <option value="bar">Bar</option>
                <option value="kitchen">Kitchen</option>
                <option value="none">None</option>
              </select>
            </Field>
            <Field label="Price (PHP)">
              <input type="number" step="0.01" value={form.price} onChange={(e)=>setForm({...form, price: e.target.value})} required data-testid="new-product-price" className="input"/>
            </Field>
            <Field label="SKU (optional)">
              <input value={form.sku} onChange={(e)=>setForm({...form, sku: e.target.value})} className="input"/>
            </Field>
            <Field label="Description">
              <textarea value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} rows={3} className="input"/>
            </Field>
            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setCreating(false)} className="h-11 px-4 rounded-lg bg-zinc-900 border border-white/10 text-zinc-300 flex-1">Cancel</button>
              <button type="submit" data-testid="new-product-submit" className="h-11 px-4 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-bold flex-1">Create</button>
            </div>
            <style>{`.input { margin-top: 6px; height: 44px; width: 100%; border-radius: 8px; background: rgb(9 9 11); border: 1px solid rgba(255,255,255,0.10); padding: 0 14px; color: #fff; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-4">
      <div className="tiny-label">{label}</div>
      {children}
    </label>
  );
}
